/**
 * Insight score and feedback vocabulary (epic #680).
 *
 * Kept out of the components so the page, the badge, the drawer and the
 * dashboard widget can never disagree about what "red" means or how the three
 * levels order.
 */

export type InsightScore = "green" | "yellow" | "red";
export type InsightStatus = "open" | "validated" | "dismissed";

export const INSIGHT_SCORES: InsightScore[] = ["red", "yellow", "green"];

/** Severity order, red first — the order a person wants to read them in. */
export const SCORE_PRIORITY: Record<InsightScore, number> = {
  red: 3,
  yellow: 2,
  green: 1,
};

/**
 * Maps a score onto a `Badge` variant rather than a colour.
 *
 * The variants are added once to the badge primitive; pages compose them and
 * never pass their own colour classes, so a theme change lands everywhere at
 * once — and dark mode keeps working.
 */
export const SCORE_BADGE_VARIANT: Record<InsightScore, "destructive" | "warning" | "success"> = {
  red: "destructive",
  yellow: "warning",
  green: "success",
};

export const INSIGHT_STATUSES: InsightStatus[] = ["open", "validated", "dismissed"];

export interface InsightLink {
  inspectionID: string;
  resourceID?: string;
  inspectionNumber?: string | null;
  inspectionDate?: string | null;
  resourceName?: string | null;
}

export interface Insight {
  fingerprint: string;
  title: string;
  description: string;
  score: InsightScore;
  confidence: number;
  links: InsightLink[];
  recommendations: string[];
  snippets?: string[];
  clusterSize?: number;
  resourceCount?: number;
  inspectionCount?: number;
  modalities?: Record<string, number>;
  status?: InsightStatus;
  feedback?: { status: InsightStatus; note?: string; at?: string } | null;
}

export interface InsightSummary {
  total: number;
  byScore: Record<InsightScore, number>;
  lastUpdated: string | null;
  lastRunId: string | null;
  /** `never-run`, `insufficient-data`, `no-clusters`, or null when graded. */
  reason: string | null;
}
